import React, { useState, useEffect } from 'react'
import * as Styled from './profile-modal.styled'
import { useTranslation } from 'react-i18next'
import UiInfoModal from 'ui/info-modal/ui-info-modal'
import PaginatorUI from 'ui/search/paginator/paginator'
import { type ProfileShortInterface } from 'data-access/market/type/market-profile-type'
import { dataAccessProfiles } from 'data-access/market/data-access-profiles'
import { Skeleton } from '@mui/material'
import EmptyBlock from 'ui/empty-block/empty-block'
import { MAX_INITIAL_PROFILES } from 'feature/market/market-provider/market-provider'

interface ProfileModalProps {
  open: boolean
  onClose: () => void
  profiles?: ProfileShortInterface[]
  categoryId?: string
}

const ProfileModal: React.FC<ProfileModalProps> = ({ open, onClose, profiles, categoryId }) => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const initialProfiles = profiles?.slice(0, MAX_INITIAL_PROFILES) ?? [] // Initialize with the first 20 profiles if available
  const [records, setRecords] = useState<{ items: ProfileShortInterface[], total: number }>({
    items: initialProfiles,
    total: initialProfiles.length
  })
  const [page, setPage] = useState<number>(1)
  const perPage = MAX_INITIAL_PROFILES
  const showNext = React.useRef<boolean>(false)
  const prevPageRef = React.useRef<number>(1) // Store the previous page number to revert if catching an error
  const skipNextLoadRef = React.useRef(false) // Flag to skip the next load if catching an error
  const { total, items } = records

  const loadData = async (categoryId: string): Promise<void> => {
    try {
      setIsLoading(true)
      const data = await dataAccessProfiles.loadProfiles(
        { page, perPage, relatedCategoryId: categoryId }
      )
      setRecords({
        items: showNext.current ? [...items, ...data.items] : data.items,
        total: data.total
      })
      showNext.current = false
    } catch (error) {
      skipNextLoadRef.current = true // Set the flag to skip the next load
      setPage(prevPageRef.current) // Revert to the previous page if an error occurs
      console.error('Profiles loading error:', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!categoryId) return
    if (profiles && profiles.length <= MAX_INITIAL_PROFILES) return

    if (!skipNextLoadRef.current) {
      void loadData(categoryId)
    } else {
      skipNextLoadRef.current = false // Reset the flag after the load attempt when error is caught
    }
  }, [categoryId, page, profiles])

  const onNextClick = (): void => {
    prevPageRef.current = page // Store the current page before incrementing
    showNext.current = true
    setPage(page => page + 1)
  }

  const handlePageChange = (newPage: number): void => {
    prevPageRef.current = page // Store the current page before changing
    setPage(newPage)
  }

  // Calculate the number of items to show in the loader based on the current page and total records
  const isFirstPage = page === 1
  const shortage = isFirstPage ? Math.max(0, perPage - initialProfiles.length) : 0

  const loaderItems = Math.min(
    perPage,
    total - ((page - 1) * perPage),
    shortage || perPage
  )

  const Loader: React.FC = () => <>
    {[...Array(loaderItems)].map((_, i) =>
      <Skeleton
        key={i}
        variant="rounded"
        width={`${Math.floor(Math.random() * (100 - 60 + 1)) + 60}%`} // Random width between 60% and 100%
        height={20}
      />
    )}
  </>

  return (
    <UiInfoModal
      open={open}
      title={t('Вибір профілю товара')}
      onClose={onClose}
      buttons={{
        preset: 1,
        items: [{
          text: t('Скасувати'),
          format: 'transparent'
        }]
      }}
    >
      <Styled.ProfileWrapper>
        {!isLoading && !total &&
          <EmptyBlock
            title={t('Профілів не знайдено')}
            text={t('За цими характеристиками нічого не знайдено.')}
          />
        }
        {((isLoading && showNext.current) || !isLoading) &&
          items.map(profile => (
            <Styled.Profile
              key={profile.id}
              onClick={onClose}
              to={`/market/profile/${profile.id}`}
            >
              {profile.title}
            </Styled.Profile>
          ))
        }
        {isLoading && <Loader />}
      </Styled.ProfileWrapper>
      {!isLoading &&
        <PaginatorUI
          total={total}
          onNextClick={onNextClick}
          page={page}
          perPage={perPage}
          onPageChange={handlePageChange}
          variant='compact'
        />
      }
    </UiInfoModal>
  )
}

export default ProfileModal
